import { useLayoutEffect, useRef, useState } from 'react';

import { mensagemDeErro } from '@/lib/erros';
import { toast } from '@/ui/primitives/aviso';
import { type RegraClassificacao } from '@fiscoai/contracts';

import { useOrdenarRegras } from './api';

/** Ids na ordem nova, com `id` levado para a posição `destino` (os outros escorregam). */
export function moverPara(ids: readonly string[], id: string, destino: number): string[] {
  const resto = ids.filter((i) => i !== id);
  const alvo = Math.max(0, Math.min(destino, resto.length));
  return [...resto.slice(0, alvo), id, ...resto.slice(alvo)];
}

/**
 * Reordenar por ↑/↓ (teclado) e por arrastar. Depois da troca, o foco volta para a mesma seta da
 * regra que andou — ou para a outra, se aquela ficou desabilitada no topo/fim.
 */
export function useReordenar(empresaId: string, regras: readonly RegraClassificacao[]) {
  const ordenar = useOrdenarRegras(empresaId);
  const linhas = useRef(new Map<string, HTMLLIElement>());
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [alvo, setAlvo] = useState<string | null>(null);
  const ids = regras.map((r) => r.id);

  const focar = (id: string, seta: 'subir' | 'descer') => {
    const linha = linhas.current.get(id);
    const outra = seta === 'subir' ? 'descer' : 'subir';
    const botao = (s: string) =>
      linha?.querySelector<HTMLButtonElement>(`button[data-mover="${s}"]:not(:disabled)`);
    (botao(seta) ?? botao(outra))?.focus();
  };

  // A lista troca um tick depois do clique: o foco volta quando a ordem nova chega à tela.
  const focoPendente = useRef<{ id: string; seta: 'subir' | 'descer' } | null>(null);
  const ordem = ids.join();
  useLayoutEffect(() => {
    const pendente = focoPendente.current;
    if (!pendente) return;
    focoPendente.current = null;
    focar(pendente.id, pendente.seta);
  }, [ordem]);

  const gravar = (novos: string[]) => {
    if (novos.join() === ordem) return;
    ordenar.mutate({ ids: novos }, { onError: (erro) => toast.error(mensagemDeErro(erro)) });
  };

  const mover = (id: string, direcao: -1 | 1) => {
    focoPendente.current = { id, seta: direcao === -1 ? 'subir' : 'descer' };
    gravar(moverPara(ids, id, ids.indexOf(id) + direcao));
  };

  const arraste = (id: string) => ({
    alvo: alvo === id && arrastando !== id,
    aoComecar: () => {
      setArrastando(id);
    },
    aoPassar: () => {
      if (alvo !== id) setAlvo(id);
    },
    aoSoltar: () => {
      if (arrastando) gravar(moverPara(ids, arrastando, ids.indexOf(id)));
      setArrastando(null);
      setAlvo(null);
    },
    aoTerminar: () => {
      setArrastando(null);
      setAlvo(null);
    },
  });

  const registrar = (id: string) => (no: HTMLLIElement | null) => {
    if (no) linhas.current.set(id, no);
    else linhas.current.delete(id);
  };

  return { mover, arraste, registrar };
}
